/**
 * AppSidebar — the CHARP dark-indigo navigation rail.
 *
 * A fixed, full-height sidebar (≈240px) rendered against `bg-sidebar`. Replaces
 * the old top `MainNavBar`. It is framework-agnostic: the router `Link` is
 * injected, exactly as before. Items are grouped into sections via inline
 * `{ section: "Label" }` separator entries.
 *
 * Active item = subtle `bg-sidebar-accent` fill + a mustard left marker
 * (`bg-sidebar-primary`). Hover = `bg-sidebar-accent`. The brand mustard is used
 * only for the active marker and numeric badges — never as a generic hover.
 *
 * On < lg the rail collapses into a Sheet triggered by a hamburger.
 */

import React, { useMemo, useState, useSyncExternalStore } from "react";
import { Avatar, AvatarFallback } from "../../base/avatar";
import { Sheet, SheetContent, SheetTrigger } from "../../base/sheet";
import { cn } from "../../../lib/utils";
import { useEnvironment } from "../../../hooks/use-environment";
import { useFeatureFlags } from "../../../hooks/use-feature-flag";
import { useNavExpanded, type UseNavExpandedResult } from "../../../hooks/use-nav-expanded";
import { Menu, LogOut, Search, ChevronRight } from "lucide-react";
import { CharpLogo } from "./CharpLogo";
import { OrganizationSwitcher, type OrganizationSummary } from "./OrganizationSwitcher";

/* ------------------------------------------------------------------ types -- */

/**
 * A navigation entry.
 *
 * Two shapes share the type: a regular link item (with `id`/`label`/`href`),
 * or a section separator (`{ section: "Label" }`) used to group items under an
 * uppercase header. Separator entries are not links.
 */
export interface NavItem {
  /** Unique identifier for the navigation item */
  id?: string;
  /** Display text for the navigation item */
  label?: string;
  /** Route path for navigation */
  href?: string;
  /**
   * Section separator label. When present the entry renders as an uppercase
   * group header rather than a link (other link fields are ignored).
   */
  section?: string;
  /** Whether the item requires authentication */
  requiresAuth?: boolean;
  /** Whether the item is only shown in specific environments */
  environments?: ("web" | "electron")[];
  /** Feature flag that controls visibility */
  featureFlag?: string;
  /** Additional CSS classes */
  className?: string;
  /** Icon component (optional) */
  icon?: React.ComponentType<{ className?: string }>;
  /** Roles required to see this item (e.g. ["admin", "owner"]) */
  requiredRoles?: string[];
  /** Optional numeric badge (e.g. unread count) */
  badge?: number;
  /**
   * Sub-items. A parent with children renders as a collapsible disclosure
   * button rather than a link.
   *
   * **One level only.** This is deliberately not a general tree: the rail is a
   * flat list with the occasional group, and nesting deeper would need
   * indentation rules, arrow-key navigation and a scroll strategy that the nav
   * does not otherwise want. Children of children are ignored.
   */
  children?: NavItem[];
}

type LinkComponent = React.ComponentType<{
  to: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  "aria-current"?: "page" | undefined;
}>;

export interface AppSidebarProps {
  /** Navigation items (link items + section separators) */
  items?: NavItem[];
  /** Authentication state */
  isAuthenticated?: boolean;
  /** User information */
  user?: {
    name?: string;
    email?: string;
  };
  /** Current user's role (filters items with requiredRoles, shown in the footer) */
  userRole?: string;
  /** Sign out handler */
  onSignOut?: () => void;
  /** Sign in handler */
  onSignIn?: () => void;
  /** Custom Link component for navigation */
  Link?: LinkComponent;
  /** Additional CSS classes for the rail */
  className?: string;
  /** Organizations the user belongs to (for the active-org switcher). */
  organizations?: OrganizationSummary[];
  /** The currently active organization id. */
  activeOrganizationId?: string;
  /** Switch the active organization. */
  onSwitchOrganization?: (organizationId: string) => void;
}

/* -------------------------------------------------------------- internals -- */

/**
 * Browsers fire no event for history.pushState/replaceState, so patch them once to
 * emit "pushstate"/"replacestate" events. This keeps usePathname reactive to in-app
 * navigation (TanStack Router and friends route through the history API) without
 * coupling this shared component to a specific router.
 */
function patchHistoryEvents(): void {
  if (typeof window === "undefined") return;
  const history = window.history as History & { __charpNavPatched?: boolean };
  if (history.__charpNavPatched) return;
  history.__charpNavPatched = true;

  const originalPushState = history.pushState.bind(history);
  history.pushState = (...args: Parameters<History["pushState"]>) => {
    originalPushState(...args);
    window.dispatchEvent(new Event("pushstate"));
  };

  const originalReplaceState = history.replaceState.bind(history);
  history.replaceState = (...args: Parameters<History["replaceState"]>) => {
    originalReplaceState(...args);
    window.dispatchEvent(new Event("replacestate"));
  };
}

/** Reactively track the current pathname without coupling to a router. */
function usePathname(): string {
  return useSyncExternalStore(
    (onChange) => {
      patchHistoryEvents();
      window.addEventListener("popstate", onChange);
      window.addEventListener("pushstate", onChange);
      window.addEventListener("replacestate", onChange);
      return () => {
        window.removeEventListener("popstate", onChange);
        window.removeEventListener("pushstate", onChange);
        window.removeEventListener("replacestate", onChange);
      };
    },
    () => window.location.pathname,
    () => "/",
  );
}

function matchesPath(href: string | undefined, pathname: string): boolean {
  if (!href) return false;
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * The one href the sidebar marks active: the longest that matches the path, across
 * items and their children. A bare prefix match would also light up `/deployments`
 * on `/deployments/adoption` (OTO-273).
 */
export function activeNavHref(items: NavItem[], pathname: string): string | null {
  let best: string | null = null;
  for (const item of items) {
    for (const candidate of [item, ...(item.children ?? [])]) {
      const href = candidate.href;
      if (href && matchesPath(href, pathname) && href.length > (best?.length ?? -1)) best = href;
    }
  }
  return best;
}

function isItemActive(href: string | undefined, activeHref: string | null): boolean {
  return href !== undefined && href === activeHref;
}

/**
 * A group counts as active when it, or any of its children, is the active item.
 * That is what keeps the mustard marker on the parent while the group is
 * collapsed — otherwise closing a group would appear to navigate away.
 */
function isNavItemActive(item: NavItem, activeHref: string | null): boolean {
  if (isItemActive(item.href, activeHref)) return true;
  return (item.children ?? []).some((child) => isItemActive(child.href, activeHref));
}

function initials(name?: string, email?: string): string {
  const source = (name || email || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/** Open the universal search overlay by replaying its ⌘K shortcut. */
function openUniversalSearch() {
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  window.dispatchEvent(
    new KeyboardEvent("keydown", {
      key: "k",
      metaKey: isMac,
      ctrlKey: !isMac,
      bubbles: true,
    }),
  );
}

const DefaultLink: LinkComponent = ({ to, className, children, onClick, ...rest }) => (
  <a href={to} className={className} onClick={onClick} {...rest}>
    {children}
  </a>
);

/* ---------------------------------------------------------------- markup --- */

/**
 * Shared row styling for links, children and group buttons, so a collapsed
 * group is visually indistinguishable from the flat items around it.
 */
function rowClassName(active: boolean, extra?: string): string {
  return cn(
    "group relative flex w-full items-center gap-3 rounded-md px-3 py-2 font-display text-sm transition-colors",
    "outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
    active
      ? "bg-sidebar-accent font-semibold text-sidebar-accent-foreground"
      : "font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
    extra,
  );
}

/** The mustard left marker. Children never carry it — it stays on the parent. */
function ActiveMarker({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-sidebar-primary transition-opacity",
        active ? "opacity-100" : "opacity-0",
      )}
      aria-hidden="true"
    />
  );
}

/** A single navigable row. */
function SidebarLink({
  item,
  Link,
  activeHref,
  onNavigate,
  showMarker = true,
  indented = false,
}: {
  item: NavItem;
  Link: LinkComponent;
  activeHref: string | null;
  onNavigate?: () => void;
  showMarker?: boolean;
  indented?: boolean;
}) {
  const active = isItemActive(item.href, activeHref);
  const Icon = item.icon;

  return (
    <Link
      to={item.href ?? "#"}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={rowClassName(active, cn(indented && "py-1.5 pl-9 text-[13px]", item.className))}
    >
      {showMarker && <ActiveMarker active={active} />}
      {Icon && (
        <Icon className={cn("h-[18px] w-[18px] shrink-0", active ? "opacity-100" : "opacity-85")} />
      )}
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge != null && (
        <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-sidebar-primary px-1.5 font-sans text-[11px] font-bold text-sidebar-primary-foreground">
          {item.badge}
        </span>
      )}
    </Link>
  );
}

/**
 * A collapsible group: a disclosure button over an indented list of children.
 *
 * Rendered as a `<button>` rather than a link because it navigates nowhere —
 * which also gives Enter/Space toggling for free. Arrow-key navigation between
 * items is deliberately out of scope.
 */
function SidebarNavGroup({
  item,
  Link,
  activeHref,
  onNavigate,
  expanded,
  onToggle,
}: {
  item: NavItem;
  Link: LinkComponent;
  activeHref: string | null;
  onNavigate?: () => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  const active = isNavItemActive(item, activeHref);
  const Icon = item.icon;
  // The desktop rail and the mobile Sheet both render RailBody, so deriving the
  // id from the group name would put two identical ids in the document and make
  // each button's aria-controls ambiguous. useId is unique per instance.
  const listId = React.useId();

  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={listId}
        className={rowClassName(active, item.className)}
      >
        {/* The marker sits on the parent, so a collapsed group still shows
            where you are. */}
        <ActiveMarker active={active} />
        {Icon && (
          <Icon
            className={cn("h-[18px] w-[18px] shrink-0", active ? "opacity-100" : "opacity-85")}
          />
        )}
        <span className="flex-1 truncate text-left">{item.label}</span>
        <ChevronRight
          className={cn(
            "h-4 w-4 shrink-0 transition-transform duration-150",
            expanded && "rotate-90",
          )}
          aria-hidden="true"
        />
      </button>
      <ul id={listId} hidden={!expanded} className="flex flex-col gap-0.5">
        {(item.children ?? []).map((child, index) => (
          <li key={child.id ?? child.href ?? index}>
            <SidebarLink
              item={child}
              Link={Link}
              activeHref={activeHref}
              onNavigate={onNavigate}
              showMarker={false}
              indented
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The scrollable nav body (items + section labels). Shared by rail + Sheet.
 *
 * Expansion state is passed in rather than read here: `renderRailBody()` mounts
 * this twice (the desktop rail and the mobile Sheet), so an internal `useState`
 * would give each copy its own, and toggling a group in the Sheet would leave
 * the rail stale until a remount.
 */
function SidebarNav({
  items,
  Link,
  activeHref,
  onNavigate,
  isExpanded,
  toggle,
}: {
  items: NavItem[];
  Link: LinkComponent;
  activeHref: string | null;
  onNavigate?: () => void;
} & UseNavExpandedResult) {
  return (
    <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-4">
      {items.map((item, index) => {
        if (item.section) {
          return (
            <div
              key={`section-${item.section}-${index}`}
              className={cn(
                "font-display text-[10.5px] font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/40 px-3 pb-2",
                index === 0 ? "pt-0" : "pt-5",
              )}
            >
              {item.section}
            </div>
          );
        }

        const key = item.id ?? item.href ?? index;

        if (item.children && item.children.length > 0) {
          const groupId = item.id ?? item.label ?? String(index);
          const containsActivePath = isNavItemActive(item, activeHref);
          return (
            <SidebarNavGroup
              key={key}
              item={item}
              Link={Link}
              activeHref={activeHref}
              onNavigate={onNavigate}
              expanded={isExpanded(groupId, containsActivePath)}
              onToggle={() => toggle(groupId, containsActivePath)}
            />
          );
        }

        return (
          <SidebarLink
            key={key}
            item={item}
            Link={Link}
            activeHref={activeHref}
            onNavigate={onNavigate}
          />
        );
      })}
    </nav>
  );
}

/** The pinned footer: brand search affordance, user identity + sign-out. */
function SidebarFooter({
  isAuthenticated,
  user,
  userRole,
  onSignOut,
  onSignIn,
}: Pick<AppSidebarProps, "isAuthenticated" | "user" | "userRole" | "onSignOut" | "onSignIn">) {
  if (!isAuthenticated) {
    return (
      <div className="border-t border-sidebar-border px-3 py-4">
        <button
          type="button"
          onClick={onSignIn}
          className="flex w-full items-center justify-center rounded-md border border-sidebar-border px-3 py-2 font-display text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-sidebar-border px-3 py-3">
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9">
          <AvatarFallback className="bg-sidebar-primary font-display text-[13px] font-semibold text-sidebar-primary-foreground">
            {initials(user?.name, user?.email)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate font-display text-[13px] font-medium text-sidebar-accent-foreground">
            {user?.name || user?.email || "User"}
          </div>
          {(userRole || user?.email) && (
            <div className="truncate font-sans text-[11.5px] text-sidebar-foreground/55">
              {userRole || user?.email}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onSignOut}
          aria-label="Sign out"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- export --- */

export function AppSidebar({
  items = [],
  isAuthenticated = false,
  user,
  userRole,
  onSignOut,
  onSignIn,
  Link = DefaultLink,
  className,
  organizations,
  activeOrganizationId,
  onSwitchOrganization,
}: AppSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isElectron, isWeb } = useEnvironment();
  const pathname = usePathname();

  const { flags } = useFeatureFlags();
  // Held once here, not inside SidebarNav, so the desktop rail and the mobile
  // Sheet share a single source of truth for which groups are open.
  const navExpanded = useNavExpanded();

  const visibleItems = useMemo(() => {
    const isVisible = (item: NavItem): boolean => {
      if (item.requiresAuth && !isAuthenticated) return false;

      if (item.requiredRoles && item.requiredRoles.length > 0) {
        if (!userRole || !item.requiredRoles.includes(userRole)) return false;
      }

      if (item.environments) {
        const supported = item.environments.some(
          (env) => (env === "electron" && isElectron) || (env === "web" && isWeb),
        );
        if (!supported) return false;
      }

      // `featureFlag` was declared on NavItem but never read here, so a flagged
      // item shipped visible. Honouring it is what lets a whole group ship dark.
      if (item.featureFlag && !flags[item.featureFlag]) return false;

      return true;
    };

    const filtered = items
      .filter((item) => (item.section ? true : isVisible(item)))
      .map((item) => {
        if (!item.children || item.children.length === 0) return item;
        return { ...item, children: item.children.filter(isVisible) };
      })
      // A group whose children were all filtered out is an empty disclosure —
      // drop it, the same way an orphaned section header is dropped below.
      .filter((item) => !item.children || item.children.length > 0);

    // Drop section headers that no longer precede any visible link.
    return filtered.filter((item, index) => {
      if (!item.section) return true;
      const next = filtered[index + 1];
      return Boolean(next && !next.section);
    });
  }, [items, isAuthenticated, userRole, isElectron, isWeb, flags]);

  // Visible items only: when the most specific item is hidden, its parent is the active one.
  const activeHref = useMemo(() => activeNavHref(visibleItems, pathname), [visibleItems, pathname]);

  // Internal tool: when signed out there is no app chrome — the login screen
  // renders full-screen on its own. Rendering nothing keeps the front page minimal.
  if (!isAuthenticated) return null;

  const Brand = (
    <Link
      to="/"
      className="flex items-center px-3 pb-1 pt-1 text-sidebar-accent-foreground outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring rounded-md"
      aria-label="CHARP home"
    >
      <CharpLogo className="h-6 w-auto" />
    </Link>
  );

  const SearchTrigger = isAuthenticated ? (
    <div className="px-3 pb-2 pt-3">
      <button
        type="button"
        onClick={() => {
          setMobileOpen(false);
          openUniversalSearch();
        }}
        className="flex w-full items-center gap-2 rounded-md border border-sidebar-border bg-sidebar-accent/40 px-3 py-2 font-sans text-[13px] text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">Search</span>
        <kbd className="font-sans text-[10.5px] font-medium tracking-wide text-sidebar-foreground/45">
          ⌘K
        </kbd>
      </button>
    </div>
  ) : null;

  // Render helper (called, never used as a JSX type) — lowercase so
  // react/no-unstable-nested-components doesn't read it as a nested component.
  const renderRailBody = (onNavigate?: () => void) => (
    <>
      <div className="px-3 pb-3 pt-5">{Brand}</div>
      {organizations && onSwitchOrganization && (
        <OrganizationSwitcher
          organizations={organizations}
          activeOrganizationId={activeOrganizationId}
          onSwitch={(id) => {
            onNavigate?.();
            onSwitchOrganization(id);
          }}
        />
      )}
      {SearchTrigger}
      <SidebarNav
        items={visibleItems}
        Link={Link}
        activeHref={activeHref}
        onNavigate={onNavigate}
        isExpanded={navExpanded.isExpanded}
        toggle={navExpanded.toggle}
      />
      <SidebarFooter
        isAuthenticated={isAuthenticated}
        user={user}
        userRole={userRole}
        onSignOut={onSignOut}
        onSignIn={onSignIn}
      />
    </>
  );

  return (
    <>
      {/* Desktop fixed rail */}
      <aside
        className={cn(
          "hidden lg:flex lg:w-60 lg:shrink-0 lg:flex-col lg:bg-sidebar lg:text-sidebar-foreground",
          "lg:sticky lg:top-0 lg:h-screen",
          className,
        )}
        role="navigation"
        aria-label="Primary"
      >
        {renderRailBody()}
      </aside>

      {/* Mobile hamburger + Sheet */}
      <div className="sticky top-0 z-40 flex items-center gap-3 border-b border-border bg-sidebar px-4 py-2 text-sidebar-foreground lg:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Open navigation menu"
              className="flex h-10 w-10 items-center justify-center rounded-md text-sidebar-foreground transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="flex w-[260px] flex-col gap-0 border-sidebar-border bg-sidebar p-0 text-sidebar-foreground sm:max-w-[260px]"
          >
            {renderRailBody(() => setMobileOpen(false))}
          </SheetContent>
        </Sheet>
        <CharpLogo className="h-5 w-auto text-sidebar-accent-foreground" />
      </div>
    </>
  );
}

export default AppSidebar;
